import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { thumbStyle } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Heart, ArrowLeft, Sparkles, MapPin, Calendar, Phone, RefreshCw, ZoomIn, ChevronLeft, ChevronRight, Facebook, ExternalLink } from 'lucide-react';
import { getColorLabel } from '@/utils/petColors';
import { formatPetDate } from '@/utils/formatDate';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Coral marker icon
const coralIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
      <circle cx="16" cy="16" r="10" fill="#FF8C66" stroke="#FF7A50" stroke-width="2"/>
      <circle cx="16" cy="16" r="4" fill="white"/>
    </svg>
  `),
  iconSize: [32, 32],
  iconAnchor: [16, 16],
});

const FIELD_LABELS = {
  'pet_type_dog':          '🐕 Tipo: Perro',
  'pet_type_cat':          '🐈 Tipo: Gato',
  'location_very_close':   '📍 Ubicación muy cercana (<500m)',
  'location_close':        '📍 Ubicación cercana (<1.5km)',
  'location_nearby':       '📍 Ubicación próxima (<3km)',
  'location_area':         '📍 Misma área (<5km)',
  'location_text_exact':   '📍 Misma ubicación (texto)',
  'location_text_partial': '📍 Ubicación similar (texto)',
  'date_same_day':         '📅 Misma fecha (±1 día)',
  'date_very_close':       '📅 Fecha muy cercana (±3 días)',
  'date_close':            '📅 Fecha cercana (±7 días)',
  'date_moderate':         '📅 Fecha moderada (±14 días)',
  'color_exact':           '🎨 Color exacto',
  'color_partial':         '🎨 Color parcial',
  'ai:breed_match':        '🤖 IA: Raza coincide',
  'ai:size_match':         '🤖 IA: Tamaño coincide',
  'ai:fur_type_match':     '🤖 IA: Tipo de pelaje coincide',
};

// Points awarded per matched field (mirrors backend calculate_rule_based_score)
const FIELD_POINTS = {
  'location_very_close':   { pts: 40, max: 40, category: 'location' },
  'location_close':        { pts: 32, max: 40, category: 'location' },
  'location_nearby':       { pts: 24, max: 40, category: 'location' },
  'location_area':         { pts: 16, max: 40, category: 'location' },
  'location_text_exact':   { pts: 35, max: 40, category: 'location' },
  'location_text_partial': { pts: 15, max: 40, category: 'location' },
  'date_same_day':         { pts: 20, max: 20, category: 'date' },
  'date_very_close':       { pts: 15, max: 20, category: 'date' },
  'date_close':            { pts: 10, max: 20, category: 'date' },
  'date_moderate':         { pts: 5,  max: 20, category: 'date' },
  'color_exact':           { pts: 40, max: 40, category: 'color' },
  'color_partial':         { pts: 25, max: 40, category: 'color' },
};

const CATEGORY_INFO = {
  location: { label: '📍 Ubicación', max: 40, icon: '📍' },
  date:     { label: '📅 Fecha',     max: 20, icon: '📅' },
  color:    { label: '🎨 Color',     max: 40, icon: '🎨' },
};

function buildRuleBreakdown(matchedFields) {
  const fields = (matchedFields || []).filter(
    f => !f.startsWith('reasoning:') && !f.startsWith('pet_type_mismatch') && !f.startsWith('ai:') && !f.startsWith('pet_type_')
  );
  const aiFields = (matchedFields || []).filter(f => f.startsWith('ai:'));

  // Calculate points per category
  const categories = {};
  for (const [cat, info] of Object.entries(CATEGORY_INFO)) {
    categories[cat] = { ...info, pts: 0, matchedField: null };
  }

  for (const field of fields) {
    const info = FIELD_POINTS[field];
    if (info) {
      categories[info.category].pts = info.pts;
      categories[info.category].matchedField = field;
    }
  }

  const totalPts = Object.values(categories).reduce((s, c) => s + c.pts, 0);
  const totalMax = Object.values(categories).reduce((s, c) => s + c.max, 0);

  return { categories, totalPts, totalMax, aiFields };
}

const FORMULA_LABELS = {
  'clip+ai+rules': 'IA × 60% + CLIP × 20% + Reglas × 20%',
  'ai+rules':      'IA × 80% + Reglas × 20%',
  'clip+rules':    'CLIP × 50% + Reglas × 50%',
  'rules_only':    'Reglas × 60%',
};

const AI_SKIP_LABELS = {
  'not_same_pet':       'IA determinó que no es la misma mascota',
  'not_same_species':   'IA determinó que no es el mismo tipo de animal',
  'no_photos':          'Ambas publicaciones sin foto',
  'no_photo_source':    'La publicación original no tiene foto',
  'no_photo_candidate': 'La coincidencia no tiene foto',
  'ai_error':           'Error al conectar con el servicio de IA',
  'not_in_top5':        'No entró al top 5 para comparación IA',
};

const ScoreDetailDialog = ({ match, open, onClose }) => {
  if (!match) return null;

  const { categories, totalPts, totalMax, aiFields } = buildRuleBreakdown(match.matched_fields);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-fraunces">
            Detalle del Score — {Math.round(match.similarity_score)}% match
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          {/* Formula */}
          <div className="bg-muted rounded-xl p-3">
            <p className="text-xs text-muted-foreground mb-1 font-medium">Fórmula utilizada</p>
            <p className="font-mono text-xs">{FORMULA_LABELS[match.match_type] || match.match_type}</p>
          </div>

          {/* AI Score */}
          {match.ai_score > 0 ? (
            <div className="border rounded-xl p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-medium flex items-center gap-1">
                  <Sparkles className="h-3.5 w-3.5 text-primary" />
                  Score IA
                </span>
                <Badge variant="secondary" className="rounded-full">{Math.round(match.ai_score)}%</Badge>
              </div>
              {match.ai_reasoning && (
                <p className="text-xs text-muted-foreground italic border-l-2 border-primary/30 pl-2">
                  "{match.ai_reasoning}"
                </p>
              )}
            </div>
          ) : match.ai_skip_reason && (
            <div className="border border-dashed rounded-xl p-3 space-y-1">
              <div className="flex items-center justify-between">
                <span className="font-medium flex items-center gap-1 text-muted-foreground">
                  <Sparkles className="h-3.5 w-3.5" />
                  Score IA
                </span>
                <Badge variant="outline" className="rounded-full text-muted-foreground">No aplicó</Badge>
              </div>
              <p className="text-xs text-muted-foreground">
                {AI_SKIP_LABELS[match.ai_skip_reason] || match.ai_skip_reason}
              </p>
              {match.ai_reasoning && (
                <p className="text-xs text-muted-foreground italic border-l-2 border-muted pl-2">
                  "{match.ai_reasoning}"
                </p>
              )}
            </div>
          )}

          {/* CLIP Score */}
          {match.clip_score > 0 && (
            <div className="border rounded-xl p-3 flex items-center justify-between">
              <span className="font-medium">📐 Score CLIP</span>
              <Badge variant="secondary" className="rounded-full">{Math.round(match.clip_score)}%</Badge>
            </div>
          )}

          {/* Rule Score — detailed breakdown */}
          <div className="border rounded-xl p-3 space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-medium">📋 Score Reglas</span>
              <Badge variant="secondary" className="rounded-full">{Math.round(match.rule_score)}%</Badge>
            </div>

            {/* Category breakdown */}
            <div className="space-y-2.5">
              {Object.entries(categories).map(([key, cat]) => {
                const pct = cat.max > 0 ? (cat.pts / cat.max) * 100 : 0;
                const fieldLabel = cat.matchedField ? (FIELD_LABELS[cat.matchedField] || cat.matchedField) : null;
                return (
                  <div key={key} className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-medium">{cat.label}</span>
                      <span className={`font-mono font-semibold ${cat.pts > 0 ? 'text-green-600' : 'text-muted-foreground'}`}>
                        {cat.pts}/{cat.max} pts
                      </span>
                    </div>
                    <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${
                          pct >= 80 ? 'bg-green-500' : pct >= 50 ? 'bg-yellow-500' : pct > 0 ? 'bg-orange-400' : 'bg-transparent'
                        }`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <p className={`text-[11px] ${cat.pts > 0 ? 'text-muted-foreground' : 'text-muted-foreground/50'}`}>
                      {fieldLabel
                        ? fieldLabel.replace(/^[^\s]+\s/, '')
                        : 'Sin coincidencia'}
                    </p>
                  </div>
                );
              })}
            </div>

            {/* Total rule points */}
            <div className="border-t pt-2 flex items-center justify-between text-xs font-semibold">
              <span>Total reglas</span>
              <span className="font-mono">{totalPts}/{totalMax} pts = {totalMax > 0 ? Math.round(totalPts / totalMax * 100) : 0}%</span>
            </div>

            {/* AI-detected feature matches */}
            {aiFields.length > 0 && (
              <div className="border-t pt-2 space-y-1">
                <p className="text-xs font-medium text-muted-foreground">Coincidencias IA detectadas</p>
                {aiFields.map((field, i) => (
                  <p key={i} className="text-xs text-muted-foreground flex items-center gap-1">
                    <span className="text-green-500">✓</span>
                    {FIELD_LABELS[field] || field.replace('ai:', '')}
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const PetDetail = () => {
  const navigate = useNavigate();
  const { petId } = useParams();
  const [pet, setPet] = useState(null);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [findingMatches, setFindingMatches] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(0);

  const handleLightboxKey = useCallback((e) => {
    if (!lightboxOpen || !pet?.photos?.length) return;
    if (e.key === 'ArrowRight') setLightboxIdx(i => (i + 1) % pet.photos.length);
    if (e.key === 'ArrowLeft') setLightboxIdx(i => (i - 1 + pet.photos.length) % pet.photos.length);
  }, [lightboxOpen, pet]);

  useEffect(() => {
    window.addEventListener('keydown', handleLightboxKey);
    return () => window.removeEventListener('keydown', handleLightboxKey);
  }, [handleLightboxKey]);

  useEffect(() => {
    fetchPetDetails();
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(res => {
        if (res.data?.role === 'admin') setIsAdmin(true);
        if (res.data?.user_id) setCurrentUserId(res.data.user_id);
      })
      .catch(() => {});
  }, [petId]);

  // Mostrar coincidencias ya calculadas (lectura, sin recomputar) al cargar la ficha.
  useEffect(() => {
    if (pet && pet.status !== 'resolved') {
      loadMatches();
    }
  }, [pet]);

  const fetchPetDetails = async () => {
    try {
      const response = await axios.get(`${API}/pets/${petId}`);
      setPet(response.data);
    } catch (error) {
      console.error('Error fetching pet:', error);
      toast.error('Error al cargar detalles');
    } finally {
      setLoading(false);
    }
  };

  // Lectura pública de coincidencias ya calculadas — no recomputa ni tiene costo.
  const loadMatches = async () => {
    try {
      const response = await axios.get(`${API}/matches/${petId}`, { withCredentials: true });
      setMatches(response.data.matches || []);
    } catch (error) {
      console.error('Error loading matches:', error);
    }
  };

  // Re-ejecuta el match (acción gateada: admin sin límite / dueño 1 vez por día).
  const refreshMatches = async () => {
    setFindingMatches(true);
    try {
      const response = await axios.post(`${API}/matches/find/${petId}`,
        { force_refresh: true },
        { withCredentials: true }
      );
      setMatches(response.data.matches || []);
      toast.success(
        response.data.matches?.length > 0
          ? `${response.data.matches.length} coincidencia(s) actualizada(s)`
          : 'No se encontraron coincidencias'
      );
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      if (status === 429) {
        toast.info(detail || 'Solo podés actualizar las coincidencias una vez por día.');
      } else if (status === 403) {
        toast.error(detail || 'No tenés permiso para actualizar las coincidencias.');
      } else if (status === 401) {
        toast.error('Iniciá sesión para actualizar las coincidencias.');
      } else {
        toast.error('Error al buscar coincidencias');
      }
      console.error('Error finding matches:', error);
    } finally {
      setFindingMatches(false);
    }
  };

  const isOwner = !!currentUserId && pet?.user_id === currentUserId;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center gradient-mesh">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!pet) {
    return (
      <div className="min-h-screen flex items-center justify-center gradient-mesh">
        <div className="text-center">
          <p className="text-muted-foreground">Mascota no encontrada</p>
          <Button onClick={() => navigate('/dashboard')} className="mt-4 rounded-full">
            Volver al Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen gradient-mesh">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-border">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            data-testid="back-button"
            variant="ghost"
            size="sm"
            onClick={() => navigate('/dashboard')}
            className="rounded-full"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-2">
            <Heart className="h-6 w-6 text-primary" fill="currentColor" />
            <h1 className="text-xl font-fraunces font-semibold text-foreground">
              Detalles de Mascota
            </h1>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {pet.status === 'resolved' && (
          <div className="bg-green-50 border border-green-200 rounded-2xl p-4 mb-6 text-center">
            <p className="text-green-700 font-fraunces font-semibold text-lg">
              Caso Resuelto
            </p>
            <p className="text-green-600 text-sm">
              Esta mascota fue reunida con su dueño.
            </p>
          </div>
        )}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Pet Details */}
          <div className="space-y-6">
            <Card className="rounded-3xl border-border overflow-hidden">
              {pet.photos && pet.photos.length > 0 && (
                <div
                  className="h-64 sm:h-96 overflow-hidden relative cursor-pointer group"
                  onClick={() => { setLightboxIdx(0); setLightboxOpen(true); }}
                  role="button"
                  aria-label="Ver imagen completa"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') { setLightboxIdx(0); setLightboxOpen(true); } }}
                >
                  <img
                    src={pet.photos[0]}
                    alt={pet.name || 'Pet'}
                    className="w-full h-full object-cover"
                    style={thumbStyle(pet.thumbnail_position, pet.thumbnail_zoom)}
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors flex items-center justify-center pointer-events-none">
                    <ZoomIn className="h-10 w-10 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
                  </div>
                  {pet.photos.length > 1 && (
                    <div className="absolute bottom-2 right-2 bg-black/50 text-white text-xs rounded-full px-2 py-0.5 pointer-events-none">
                      1 / {pet.photos.length}
                    </div>
                  )}
                </div>
              )}
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="font-fraunces text-2xl mb-2">
                      {pet.name || 'Sin nombre'}
                    </CardTitle>
                    <Badge
                      variant={pet.type === 'lost' ? 'destructive' : 'default'}
                      className="rounded-full"
                    >
                      {pet.type === 'lost' ? 'Perdida' : 'Encontrada'}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  {pet.pet_type && (
                    <div>
                      <p className="text-xs text-muted-foreground">Tipo</p>
                      <p className="font-medium">{pet.pet_type === 'dog' ? '🐕 Perro' : '🐈 Gato'}</p>
                    </div>
                  )}
                  {pet.breed && (
                    <div>
                      <p className="text-xs text-muted-foreground">Raza</p>
                      <p className="font-medium">{pet.breed}</p>
                    </div>
                  )}
                  {pet.color && (
                    <div>
                      <p className="text-xs text-muted-foreground">Color</p>
                      {Array.isArray(pet.color) ? (
                        <div className="flex flex-wrap gap-1 mt-0.5">
                          {pet.color.map((c) => (
                            <Badge key={c} variant="secondary" className="rounded-full text-xs">
                              {getColorLabel(c)}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <p className="font-medium">{pet.color}</p>
                      )}
                    </div>
                  )}
                  {pet.size && (
                    <div>
                      <p className="text-xs text-muted-foreground">Tamaño</p>
                      <p className="font-medium capitalize">{pet.size}</p>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground mt-1" />
                    <div className="flex-1">
                      <p className="text-xs text-muted-foreground">Ubicación</p>
                      <p className="font-medium mb-2">{pet.location}</p>

                      {/* Map showing location */}
                      {pet.latitude && pet.longitude && (
                        <div className="rounded-2xl overflow-hidden border-2 border-border" style={{ height: '250px', width: '100%' }}>
                          <MapContainer
                            center={[pet.latitude, pet.longitude]}
                            zoom={14}
                            style={{ height: '100%', width: '100%' }}
                            zoomControl={true}
                            scrollWheelZoom={false}
                          >
                            <TileLayer
                              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            />
                            <Marker position={[pet.latitude, pet.longitude]} icon={coralIcon} />
                          </MapContainer>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground mt-1" />
                    <div>
                      <p className="text-xs text-muted-foreground">
                        {pet.type === 'lost' ? 'Fecha de pérdida' : 'Fecha de encuentro'}
                      </p>
                      <p className="font-medium">
                        {formatPetDate(pet.date_lost_found, {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <p className="text-xs text-muted-foreground mb-2">Descripción</p>
                  <p className="text-sm">{pet.description}</p>
                </div>

                {pet.contact && (
                  <div className="flex items-start gap-2 bg-muted rounded-2xl p-4">
                    <Phone className="h-4 w-4 text-muted-foreground mt-1" />
                    <div>
                      <p className="text-xs text-muted-foreground">Contacto</p>
                      <p className="font-medium">{pet.contact}</p>
                    </div>
                  </div>
                )}

                {pet.url_facebook && (
                  <a
                    href={pet.url_facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-start gap-2 bg-muted rounded-2xl p-4 hover:opacity-80 transition-opacity"
                  >
                    <Facebook className="h-4 w-4 text-[#1877F2] mt-1" />
                    <div>
                      <p className="text-xs text-muted-foreground">Publicación original</p>
                      <p className="font-medium text-[#1877F2] flex items-center gap-1">
                        Ver post en Facebook
                        <ExternalLink className="h-3 w-3" />
                      </p>
                    </div>
                  </a>
                )}

                {pet.ai_features && (
                  <div className="bg-accent/10 rounded-2xl p-4">
                    <p className="text-xs text-accent-foreground font-medium mb-2 flex items-center gap-1">
                      <Sparkles className="h-3 w-3" />
                      Análisis de IA (OpenAI)
                    </p>
                    <div className="text-xs space-y-1">
                      {pet.ai_features.animal_type && (
                        <p>Tipo: {pet.ai_features.animal_type === 'dog' ? '🐕 Perro' : pet.ai_features.animal_type === 'cat' ? '🐈 Gato' : pet.ai_features.animal_type}</p>
                      )}
                      {pet.ai_features.breed && (
                        <p>Raza: {pet.ai_features.breed}</p>
                      )}
                      {pet.ai_features.colors && pet.ai_features.colors.length > 0 && (
                        <p>Colores: {pet.ai_features.colors.join(', ')}</p>
                      )}
                      {pet.ai_features.size && (
                        <p>Tamaño: {pet.ai_features.size}</p>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Matches */}
          <div className="space-y-6">
            <Card className="rounded-3xl border-border">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle className="font-fraunces text-xl flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-primary" />
                    Coincidencias
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    {pet.status !== 'resolved' && findingMatches && (
                      <span className="text-sm text-muted-foreground animate-pulse">
                        Buscando...
                      </span>
                    )}
                    {pet.status !== 'resolved' && !findingMatches && (isAdmin || isOwner) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="rounded-full text-xs text-muted-foreground hover:text-foreground"
                        onClick={refreshMatches}
                      >
                        <RefreshCw className="h-3 w-3 mr-1" />
                        Actualizar coincidencias
                      </Button>
                    )}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Las coincidencias se calculan por color, cercanía y parecido.
                </p>
              </CardHeader>
              <CardContent>
                {findingMatches ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-muted-foreground text-sm">
                      Analizando coincidencias con IA...
                    </p>
                  </div>
                ) : matches.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground text-sm">
                      No se encontraron coincidencias para esta mascota.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {matches.map((matchData, index) => (
                      <Card
                        key={index}
                        data-testid={`match-${index}`}
                        className="card-hover cursor-pointer rounded-2xl border-border overflow-hidden"
                        onClick={() => navigate(`/pet/${matchData.pet.pet_id}`)}
                      >
                        <div className="flex gap-4">
                          {matchData.pet.photos && matchData.pet.photos.length > 0 && (
                            <div className="w-24 h-24 flex-shrink-0">
                              <img
                                src={matchData.pet.photos[0]}
                                alt={matchData.pet.name || 'Pet'}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          )}
                          <div className="flex-1 py-4 pr-4">
                            <div className="flex justify-between items-start mb-2">
                              <h3 className="font-fraunces font-semibold">
                                {matchData.pet.name || 'Sin nombre'}
                              </h3>
                              <Badge
                                variant="secondary"
                                className={`rounded-full ${isAdmin ? 'cursor-pointer hover:opacity-70' : ''}`}
                                onClick={isAdmin ? (e) => { e.stopPropagation(); setSelectedMatch(matchData.match); } : undefined}
                              >
                                {Math.round(matchData.match.similarity_score)}% match
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground mb-1">
                              {matchData.pet.breed || 'Raza desconocida'} • {matchData.pet.location}
                            </p>
                            <div className="flex gap-1 flex-wrap">
                              {matchData.match.ai_score > 0 ? (
                                <Badge
                                  variant="outline"
                                  className={`text-xs rounded-full ${isAdmin ? 'cursor-pointer hover:opacity-70' : ''}`}
                                  onClick={isAdmin ? (e) => { e.stopPropagation(); setSelectedMatch(matchData.match); } : undefined}
                                >
                                  <Sparkles className="h-2 w-2 mr-1" />
                                  IA: {Math.round(matchData.match.ai_score)}%
                                </Badge>
                              ) : matchData.match.ai_skip_reason && (
                                <Badge
                                  variant="outline"
                                  className={`text-xs rounded-full text-muted-foreground border-dashed ${isAdmin ? 'cursor-pointer hover:opacity-70' : ''}`}
                                  onClick={isAdmin ? (e) => { e.stopPropagation(); setSelectedMatch(matchData.match); } : undefined}
                                >
                                  IA: —
                                </Badge>
                              )}
                              <Badge
                                variant="outline"
                                className={`text-xs rounded-full ${isAdmin ? 'cursor-pointer hover:opacity-70' : ''}`}
                                onClick={isAdmin ? (e) => { e.stopPropagation(); setSelectedMatch(matchData.match); } : undefined}
                              >
                                Reglas: {Math.round(matchData.match.rule_score)}%
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-screen-lg w-full h-[90vh] p-0 bg-black border-0 rounded-2xl overflow-hidden flex flex-col [&>button]:text-white [&>button]:opacity-70 [&>button:hover]:opacity-100">
          <div className="flex items-center justify-between px-4 py-2 text-white/80 text-sm shrink-0">
            <span className="font-fraunces font-semibold">{pet?.name || 'Mascota'}</span>
            {pet?.photos?.length > 1 && (
              <span>{lightboxIdx + 1} / {pet.photos.length}</span>
            )}
          </div>
          <div className="relative flex-1 flex items-center justify-center min-h-0">
            <img
              src={pet?.photos?.[lightboxIdx]}
              alt={`Foto ${lightboxIdx + 1}`}
              className="max-w-full max-h-full object-contain"
            />
            {pet?.photos?.length > 1 && (
              <>
                <button
                  className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
                  onClick={(e) => { e.stopPropagation(); setLightboxIdx(i => (i - 1 + pet.photos.length) % pet.photos.length); }}
                  aria-label="Foto anterior"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button
                  className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
                  onClick={(e) => { e.stopPropagation(); setLightboxIdx(i => (i + 1) % pet.photos.length); }}
                  aria-label="Siguiente foto"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
              </>
            )}
          </div>
          {pet?.photos?.length > 1 && (
            <div className="flex gap-2 px-4 py-3 overflow-x-auto shrink-0 justify-center">
              {pet.photos.map((photo, i) => (
                <button
                  key={i}
                  onClick={() => setLightboxIdx(i)}
                  className={`w-14 h-14 rounded-lg overflow-hidden shrink-0 border-2 transition-colors ${i === lightboxIdx ? 'border-primary' : 'border-transparent opacity-60 hover:opacity-100'}`}
                  aria-label={`Ver foto ${i + 1}`}
                >
                  <img src={photo} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ScoreDetailDialog
        match={selectedMatch}
        open={!!selectedMatch}
        onClose={() => setSelectedMatch(null)}
      />
    </div>
  );
};

export default PetDetail;
